import { useEffect, useState } from "react";
import { Menu, X, Phone, Languages } from "lucide-react";
import { NAV_HREFS, waLink, callLink, CLINIC } from "../data/clinic";
import { useLang } from "../i18n/LanguageContext";

export default function Header() {
  const { lang, toggle, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const NAV = [
    { label: t.nav.services, href: NAV_HREFS[0] },
    { label: t.nav.about, href: NAV_HREFS[1] },
    { label: t.nav.why, href: NAV_HREFS[2] },
    { label: t.nav.location, href: NAV_HREFS[3] },
    { label: t.nav.contact, href: NAV_HREFS[4] },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-50 w-full transition-[padding,background] duration-300 ${
        scrolled ? "glass-header py-2" : "py-4"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
        <a href="#top" data-testid="logo-link" className="flex flex-col leading-none">
          <span className="font-display text-xl tracking-tight text-brand-ink md:text-2xl">
            Kesari Dental
          </span>
          <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-brand-inksoft">
            {t.clinicShort}
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((l, i) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${["services", "about", "why", "location", "contact"][i]}`}
              className="text-sm font-medium text-brand-inksoft transition-colors hover:text-brand-primary"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label="Switch language"
            className="flex items-center gap-1.5 rounded-full border border-brand-border px-3 py-2.5 text-xs font-semibold text-brand-primary transition-colors hover:bg-brand-primary hover:text-white"
          >
            <Languages className="h-4 w-4" strokeWidth={1.5} />
            {t.langLabel}
          </button>
          <a
            href={callLink}
            data-testid="header-call-btn"
            aria-label={`Call ${CLINIC.phone}`}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-border text-brand-primary transition-colors hover:bg-brand-primary hover:text-white"
          >
            <Phone className="h-5 w-5" strokeWidth={1.5} />
          </a>
          <a
            href={waLink(null, lang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-btn"
            className="hidden rounded-full bg-brand-warm px-6 py-3 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-warmHover sm:inline-flex"
          >
            {t.common.book}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-border text-brand-ink lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="glass-header mt-2 lg:hidden" data-testid="mobile-menu">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
            {NAV.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={close}
                className="rounded-xl px-3 py-3 text-base font-medium text-brand-ink transition-colors hover:bg-brand-bg"
              >
                {l.label}
              </a>
            ))}
            <a
              href={waLink(null, lang)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              data-testid="mobile-book-btn"
              className="mt-2 rounded-full bg-brand-warm px-6 py-3 text-center text-sm font-semibold text-white"
            >
              {t.common.bookWhatsApp}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
